import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { Subscription } from 'rxjs'
import { wait } from 'src/utils'
import { Objects } from '../../../../server/src/shared-with-client/object-definitions'
import { ParseService } from '../parse.service'
import { AppBaseButtonComponent } from '../shared/designs-v2/app-button/app-base-button.component'
import { CustomInputComponent } from '../shared/designs-v2/outlined-input-field/outlined-input-field.component'
import { SvgIconComponent } from '../shared/designs-v2/svg-icon/svg-icon.component'
import { NotificationService } from '../shared/services/notification.service'
import { FormControlUtil } from '../shared/utils/form-control.util'
import { StringUtil } from '../shared/utils/string.util'

type UtmLoginStorageKey = 'wise-pelican/utm-login'

@Component({
    selector: 'app-login-v2',
    templateUrl: './login-component-v2.component.html',
    styleUrls: ['./login-component-v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, SvgIconComponent, CustomInputComponent, AppBaseButtonComponent],
})
export class LoginV2Component implements AfterViewInit, OnInit, OnDestroy {
    isLoggingIn = false
    forgotPassword = false
    isResettingPassword = false
    private _subscriptions: Subscription[] = []

    loginFormControls = {
        email: new FormControl<string>('', {
            validators: [Validators.required, Validators.email, Validators.maxLength(256)],
            updateOn: 'submit',
        }),
        password: new FormControl<string>('', {
            validators: [Validators.required],
            updateOn: 'submit',
        }),
    }

    loginForm: FormGroup = new FormGroup(this.loginFormControls)

    resetPasswordFormControl = {
        email: new FormControl<string>('', {
            validators: [Validators.required, Validators.email],
            updateOn: 'submit',
        }),
    }
    resetPasswordForm: FormGroup = new FormGroup(this.resetPasswordFormControl, { updateOn: 'submit' })

    @ViewChild('emailLoginElement') emailLoginElement?: CustomInputComponent
    @ViewChild('emailResetElement') emailResetElement?: CustomInputComponent

    constructor(
        private parseService: ParseService,
        private router: Router,
        public element: ElementRef<HTMLElement>,
        private route: ActivatedRoute,
        private _cdRef: ChangeDetectorRef,
        private notificationService: NotificationService
    ) {}

    ngOnInit() {
        this._subscriptions.push(
            this.route.queryParams.subscribe((params) => {
                this.forgotPassword = params['reset-password']
            })
        )
    }

    ngOnDestroy(): void {
        this._subscriptions.forEach((sub) => sub.unsubscribe())
    }

    private triggerChangeDetection() {
        this._cdRef.detectChanges()
    }

    private getFinalRedirectUrl(redirectUrl: string): string {
        if (!redirectUrl) {
            return ''
        }

        if (redirectUrl.startsWith('/login')) {
            const nestedRedirect = redirectUrl.split('/login?')[1]
            const includesNestedRedirect = !!nestedRedirect
            if (includesNestedRedirect) {
                const nestedRedirectParsed = nestedRedirect.split('redirectUrl=')[1] ?? ''
                return this.getFinalRedirectUrl(decodeURIComponent(nestedRedirectParsed))
            } else {
                return ''
            }
        } else {
            return redirectUrl
        }
    }

    ngAfterViewInit(): void {
        this.focusElement('loginEmail')
        this.handleUtmParameters()
    }

    private handleUtmParameters(): void {
        try {
            const queryParamsSnapshot = this.route.snapshot.queryParams

            if (queryParamsSnapshot) {
                const key: UtmLoginStorageKey = 'wise-pelican/utm-login'
                const existingParamString = localStorage.getItem(key)
                const utmParams: Objects.Establishment.UtmParameters = existingParamString ? JSON.parse(existingParamString) : {}

                if (queryParamsSnapshot['utm_campaign']) {
                    utmParams.campaign = queryParamsSnapshot['utm_campaign']
                }
                if (queryParamsSnapshot['utm_content']) {
                    utmParams.content = queryParamsSnapshot['utm_content']
                }
                if (queryParamsSnapshot['utm_medium']) {
                    utmParams.medium = queryParamsSnapshot['utm_medium']
                }
                if (queryParamsSnapshot['utm_source']) {
                    utmParams.source = queryParamsSnapshot['utm_source']
                }
                if (queryParamsSnapshot['utm_term']) {
                    utmParams.term = queryParamsSnapshot['utm_term']
                }
                if (queryParamsSnapshot['utm_device']) {
                    utmParams.device = queryParamsSnapshot['utm_device']
                }

                if (Object.keys(utmParams).length > 0) {
                    const value = JSON.stringify(utmParams)
                    localStorage.setItem(key, value)
                }
            }
        } catch (err) {
            this.parseService.reportUIError(err, 'LoginComponent.handleUtmParameters()', '')
        }
    }

    getControlErrorMessage(control: AbstractControl, controlName: string): string {
        const errors = control.errors
        if (!errors) {
            return ''
        }

        const errorMessages: { [key: string]: string } = {
            required: `${StringUtil.toTitleCase(controlName)} is required`,
            email: 'Please enter a valid email address',
            maxlength: 'Email must be no more than 256 characters.',
        }

        return errorMessages[Object.keys(errors)[0]] || 'Invalid value'
    }

    validateFormFields(form: FormGroup) {
        FormControlUtil.markFormGroupsAsTouched(form)
        if (!this.loginForm.valid) {
            this.notificationService.showError('Please fill in the missing fields')
        }
    }

    private getTargetPath(redirectUrl: string, roles: Objects.Role.Parse[]): string {
        if (redirectUrl && redirectUrl !== '/login') return redirectUrl
        if (roles.length === 1 && (roles[0]?.get('name') === 'ProFulfillment Customer' || roles[0]?.get('name') === 'User')) return '/tracking'
        return '/products'
    }

    private async retryLogin(user: Objects.User._Parse, retryCount = 2): Promise<Objects.Role.Parse[]> {
        let roles = await this.parseService.roleService.find.toPromise({ user })

        for (let i = 0; i < retryCount && roles.length === 0; i++) {
            await wait((i + 1) * 1000)
            this.parseService.reportUIWarning(new Error(`${i + 2}nd Login Attempt`), 'LoginComponent.onLoginButtonClicked', '')
            this.parseService.roleService.clearRoles()
            this.parseService.roleService.find.resetAll()
            roles = await this.parseService.roleService.find.toPromise({ user })
        }

        return roles
    }

    async onLoginButtonClicked() {
        const email = this.loginFormControls.email.value?.trim().toLowerCase() || ''
        const password = this.loginFormControls.password.value || ''
        const trimmedPassword = password.trim()

        if (!trimmedPassword) {
            if (trimmedPassword !== password) {
                this.loginFormControls.password.setValue(null)
            }
            this.loginFormControls.password.setErrors({ required: true })
        }

        if (!this.loginForm.valid) {
            this.validateFormFields(this.loginForm)
            return
        }

        try {
            this.isLoggingIn = true
            const user = await this.parseService.logIn(email, trimmedPassword, { updateDefaultEstablishment: true })
            let roles = await this.parseService.roleService.find.toPromise({ user })

            if (roles.length === 0) {
                roles = await this.retryLogin(user)
            }

            const redirectUrl = this.getFinalRedirectUrl(this.route.snapshot.queryParamMap.get('redirectUrl') ?? '')
            const useRouter = roles.length > 0
            const targetPath = this.getTargetPath(redirectUrl, roles)
            if (useRouter) {
                await this.router.navigateByUrl(targetPath)
            } else {
                window.open(location.origin + targetPath, '_parent')
            }
        } catch (err) {
            const ignoreReportingErrorsList = ['Invalid username/password.', 'User Account Disabled']
            const message = this.parseService.getErrorMessage(err)
            if (ignoreReportingErrorsList.indexOf(err?.message) === -1) {
                this.parseService.reportUIError(err, 'LoginComponent.onLoginButtonClicked()', JSON.stringify({ email }))
            }

            this.isLoggingIn = false
            this.notificationService.showError(
                message === 'Invalid username/password.' ? 'Invalid email or password.' : message || 'Error Logging In. Please Reset Password'
            )
            this.triggerChangeDetection()
        }
    }

    async onResetPasswordButtonClick() {
        const email: string = this.resetPasswordFormControl.email.value?.trim().toLowerCase() || ''
        const emailTrimmedLowerCase = email.trim().toLowerCase()
        if (!emailTrimmedLowerCase) {
            this.resetPasswordFormControl.email.setErrors({ required: true })
            this.validateFormFields(this.resetPasswordForm)
            return
        }

        if (!this.resetPasswordForm.valid) {
            this.validateFormFields(this.resetPasswordForm)
            return
        }

        try {
            this.isResettingPassword = true

            await this.parseService.logOut()
            await this.parseService.requestPasswordReset(emailTrimmedLowerCase)
            this.notificationService.showSuccess(
                'Check your inbox for reset instructions. If your email is linked to a Wise Pelican account, you’ll receive an email shortly. Don’t forget to check your spam folder.'
            )
            this.onReturnToLoginFooterClick()
        } catch (err) {
            const message = this.parseService.getErrorMessage(err)
            const warningMessageList = ['No User Found']

            const isWarning = warningMessageList.indexOf(message) > -1
            if (isWarning) {
                this.parseService.reportUIWarning(err, 'LoginComponent.resetPassword()', '')
            } else {
                this.parseService.reportUIError(err, 'LoginComponent.resetPassword()', '')
            }

            let forgotMessage = 'Error Submitting Request. Please Contact Wise Pelican'
            if (message) {
                forgotMessage = message
            }
            this.notificationService.showError(forgotMessage)
        }

        this.isResettingPassword = false
        this.triggerChangeDetection()
    }

    removeResetPasswordQueryParam() {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { 'reset-password': null }, // Removes the 'resetPassword' query param
            queryParamsHandling: 'merge',
        })
    }

    onReturnToLoginFooterClick() {
        this.forgotPassword = false
        this.loginForm.reset()
        this.removeResetPasswordQueryParam()
        this.focusElement('loginEmail')
    }

    onResetPasswordFooterClick() {
        this.forgotPassword = true
        this.resetPasswordForm.reset()
        this.focusElement('resetEmail')
    }

    private focusElement(elementName: 'loginEmail' | 'resetEmail') {
        setTimeout(() => {
            const element = elementName === 'loginEmail' ? this.emailLoginElement : elementName === 'resetEmail' ? this.emailResetElement : undefined
            if (element) {
                element.focus()
            }
        }, 200)
    }

    isLoginButtonDisabled(): boolean {
        return this.isLoggingIn || this.isResettingPassword
    }
}
